import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Play, Pause } from 'lucide-react';
import { mediaUrl } from '../../lib/api';
import { useE2E } from '../../context/E2EContext';

/**
 * Voice-note / audio-file bubble.
 * - Fetches the file (decrypting when the message is E2E)
 * - Plays via `<audio>` with proper iOS/Safari attributes
 * - Shows a red "unplayed" dot for incoming audios that were never opened
 */
export default function AudioBubble({ msg, isOut }) {
  const url = msg.file_url;
  const msgId = msg.id;
  const e2e = useE2E();
  const audioRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(false);
  const [everPlayed, setEverPlayed] = useState(() => {
    try {
      const played = JSON.parse(localStorage.getItem('gtc_audio_played') || '[]');
      return played.includes(msgId);
    } catch { return false; }
  });

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const { data, headers } = await axios.get(mediaUrl(url), { responseType: 'blob' });
        if (cancel) return;
        let finalBlob = data;
        if (msg.encrypted && msg.crypto?.file_key_wrapped && e2e?.ready) {
          const peerId = msg.sender_id;
          const mime = msg.file_type || 'audio/webm';
          finalBlob = await e2e.decryptFileFromPeer(peerId, data, msg.crypto, mime);
        } else {
          const ct = (headers && (headers['content-type'] || headers['Content-Type'])) || data.type || 'audio/webm';
          if (!data.type) finalBlob = new Blob([data], { type: ct });
        }
        if (cancel) return;
        setBlobUrl(URL.createObjectURL(finalBlob));
        setLoading(false);
      } catch { if (!cancel) { setLoading(false); setError(true); } }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [url, msg.id, e2e?.ready]);

  // Revoke blob URL only when the component unmounts or url changes
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const markPlayed = () => {
    if (everPlayed || !msgId) return;
    try {
      const played = JSON.parse(localStorage.getItem('gtc_audio_played') || '[]');
      if (!played.includes(msgId)) {
        played.push(msgId);
        while (played.length > 500) played.shift();
        localStorage.setItem('gtc_audio_played', JSON.stringify(played));
      }
      setEverPlayed(true);
    } catch { /* noop */ }
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a || !blobUrl) return;
    markPlayed();
    try {
      if (a.paused) {
        a.muted = false;
        a.volume = 1;
        await a.play();
      } else {
        a.pause();
      }
    } catch {
      setPlaying(false);
    }
  };

  const fmt = (s) => {
    if (!isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };
  const pct = duration ? Math.min(100, (progress / duration) * 100) : 0;
  const showUnplayed = !isOut && !everPlayed;

  return (
    <div className="flex items-center gap-3 min-w-[200px]" data-testid="msg-audio">
      <div className="relative">
        <button
          data-testid="audio-play-btn"
          onClick={toggle}
          disabled={!blobUrl || error}
          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition ${isOut ? 'bg-black/20' : 'bg-white/10'} disabled:opacity-40`}
        >
          {loading ? (
            <span className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
          ) : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        {showUnplayed && (
          <span
            data-testid="audio-unplayed-dot"
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-[var(--bg-void)] pointer-events-none"
            aria-label="Non ascoltato"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`h-1 rounded-full overflow-hidden ${isOut ? 'bg-black/20' : 'bg-white/15'}`}>
          <div className={`h-full ${isOut ? 'bg-black/50' : 'bg-[var(--gold)]'} transition-[width] duration-100`} style={{ width: `${pct}%` }} />
        </div>
        <div className={`text-[10px] mt-0.5 tabular-nums ${isOut ? 'text-black/70' : 'text-white/50'}`}>
          {error ? 'Formato non supportato' : fmt(playing || progress > 0 ? progress : duration)}
        </div>
      </div>
      {blobUrl && (
        <audio
          ref={audioRef}
          src={blobUrl}
          preload="auto"
          playsInline
          controls={false}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onDurationChange={(e) => setDuration(e.currentTarget.duration || 0)}
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime || 0)}
          onPlay={() => { setPlaying(true); markPlayed(); }}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setProgress(0); }}
          onError={() => { setPlaying(false); setError(true); }}
        />
      )}
    </div>
  );
}
